import React, { Key, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import {
  LockboxType,
  brandMap,
  ESLockbox,
  LockboxTypes,
  LockboxSearchForMLSStaffResponse,
  SearchForESLockboxQuery,
  lockboxTypeMap,
} from '@bb/domain/dist/types';
import {
  OwnershipStatuses,
  OwnershipStatusType,
  LockboxStatuses,
  LockboxStatusType,
  ResaleStatuses,
  ResaleStatusType,
} from '@bb/domain/dist/types/SupraLockbox';
import {
  Avatar,
  Button,
  Checkbox,
  Input,
  ListingImage,
  message,
  Modal,
  TableProps,
  Text,
  theme,
} from 'components';
import { useIsFirstRender } from 'hooks';
import LockboxLicenseTag from 'components/LockboxLicenseTag';
import ResaleStatusTag from 'components/ResaleStatusTag';
import LockboxStatusTag from 'components/LockboxStatusTag';
import { getMLSLockboxes } from 'api/get';
import { deleteLockbox } from 'api/LockboxController';
import { LockboxTypeFilterOptions } from 'api/AnsweringOrgController/getLockboxTypeFilterOptions';
import errorReporter from '@app/errorReporter';
import { BrandLogo } from 'components/BrandLogo';
import { useHistory } from 'react-router-dom';
import { debounce, isEqual } from 'lodash';
import { Loading } from '@app/react-common';
import { useSearchParams } from '../../../AnsweringSearchPage/hooks';
import { useMLSStaffContext } from '../../MLSStaffContext';
import SearchTable from '../../SearchTable';
import { ActionText } from '../../styles';
import { TableSortType } from '../../types';
import {
  StyledFilterBottom,
  StyledFilterListing,
  StyledFilterSearchResult,
  StyledFilterUser,
} from '../../../LockboxesActivityPage/LockboxActivityTable';

const Unassigned: React.FC = () => (
  <div style={{ color: theme.alpha45 }}>Unassigned</div>
);

const arrayIfHasElements = (arr?: any[]) => (arr?.length ? arr : undefined);

const MLSStaffLockboxesTable: React.FC = () => {
  const isFirstRender = useIsFirstRender();
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [lockboxToDelete, setLockboxToDelete] = React.useState<ESLockbox>();
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [filterSearchLoading, setFilterSearchLoading] = React.useState(false);
  const history = useHistory();
  const {
    loading,
    setLoading,
    page,
    setPage,
    text: debouncedText,
    setInitialTotal,
    setTotal,
    setCsvLink,
    setSelectedLockboxes,
    lockboxTypeFilterOptions,
    setMlsLockboxes,
  } = useMLSStaffContext();
  const [searchResult, setSearchResult] =
    React.useState<LockboxSearchForMLSStaffResponse>();
  const [searchParam, setSearchParam] = useSearchParams<{
    search?: string;
    lockboxTypeFilter?: LockboxType[];
    listingsFilter?: string[];
    ownersFilter?: string[];
    usersFilter?: string[];
    orgsFilter?: string[];
    ownershipStatusesFilter?: (OwnershipStatusType | '(Blank)')[];
    resaleStatusesFilter?: (ResaleStatusType | '(Blank)')[];
    lockboxStatusesFilter?: (LockboxStatusType | '(Blank)')[];
    sort?: TableSortType;
  }>();

  const [sort, setSort] = React.useState<TableSortType>({
    ...searchParam.sort,
  });

  // #region Listing filter
  const [listingInput, setListingInput] = React.useState('');
  const [selectedListings, setSelectedListings] = React.useState<
    Record<string, NonNullable<ESLockbox['listing']>>
  >({});
  const [searchedListings, setSearchedListings] = React.useState<
    NonNullable<ESLockbox['listing']>[]
  >([]);
  const [listingsFilter, setListingsFilter] = useState(
    searchParam.listingsFilter || [],
  );

  useEffect(() => {
    if (isFirstRender) return;
    if (!isEqual(Object.keys(selectedListings), listingsFilter)) {
      setListingsFilter(Object.keys(selectedListings));
    }
  }, [selectedListings]);
  // #endregion

  // #region Owner filter
  const [ownerInput, setOwnerInput] = React.useState('');
  const [selectedOwners, setSelectedOwners] = React.useState<
    Record<string, NonNullable<ESLockbox['owner']>>
  >({});
  const [searchedOwners, setSearchedOwners] = React.useState<
    NonNullable<ESLockbox['owner']>[]
  >([]);
  const [ownersFilter, setOwnersFilter] = useState(
    searchParam.ownersFilter || [],
  );

  useEffect(() => {
    if (isFirstRender) return;
    if (!isEqual(Object.keys(selectedOwners), ownersFilter)) {
      setOwnersFilter(Object.keys(selectedOwners));
    }
  }, [selectedOwners]);
  // #endregion

  // #region Assignee filter
  const [userInput, setUserInput] = React.useState('');
  const [selectedUsers, setSelectedUsers] = React.useState<
    Record<string, NonNullable<ESLockbox['assignee']>>
  >({});
  const [searchedUsers, setSearchedUsers] = React.useState<
    NonNullable<ESLockbox['assignee']>[]
  >([]);
  const [usersFilter, setUsersFilter] = useState(searchParam.usersFilter || []);

  useEffect(() => {
    if (isFirstRender) return;
    if (!isEqual(Object.keys(selectedUsers), usersFilter)) {
      setUsersFilter(Object.keys(selectedUsers));
    }
  }, [selectedUsers]);
  // #endregion

  // #region Org filter
  const [orgInput, setOrgInput] = React.useState('');
  const [selectedOrgs, setSelectedOrgs] = React.useState<
    Record<string, NonNullable<ESLockbox['org']>>
  >({});
  const [searchedOrgs, setSearchedOrgs] = React.useState<
    NonNullable<ESLockbox['org']>[]
  >([]);
  const [orgsFilter, setOrgsFilter] = useState(searchParam.orgsFilter || []);

  useEffect(() => {
    if (isFirstRender) return;
    if (!isEqual(Object.keys(selectedOrgs), orgsFilter)) {
      setOrgsFilter(Object.keys(selectedOrgs));
    }
  }, [selectedOrgs]);
  // #endregion

  const [lockboxTypeFilter, setLockboxTypeFilter] = React.useState<
    LockboxType[]
  >(searchParam.lockboxTypeFilter || []);
  const [ownershipStatusesFilter, setOwnershipStatusesFilter] = React.useState<
    (OwnershipStatusType | '(Blank)')[]
  >(searchParam.ownershipStatusesFilter || []);
  const [resaleStatusesFilter, setResaleStatusesFilter] = React.useState<
    (ResaleStatusType | '(Blank)')[]
  >(searchParam.resaleStatusesFilter || []);
  const [lockboxStatusesFilter, setLockboxStatusesFilter] = React.useState<
    (LockboxStatusType | '(Blank)')[]
  >(searchParam.lockboxStatusesFilter || []);

  const searchDeps: React.DependencyList = [
    debouncedText,
    lockboxTypeFilter,
    listingsFilter,
    ownersFilter,
    usersFilter,
    orgsFilter,
    ownershipStatusesFilter,
    resaleStatusesFilter,
    lockboxStatusesFilter,
    sort?.field,
    sort?.direction,
  ];

  const MLS_LOCKBOX_SEARCH_QUERY: SearchForESLockboxQuery = {
    searchTerm: debouncedText,
    lockboxTypes: lockboxTypeFilter,
    listings: listingsFilter,
    owners: ownersFilter,
    assignees: usersFilter,
    orgs: orgsFilter,
    ownershipStatuses: ownershipStatusesFilter,
    resaleStatuses: resaleStatusesFilter,
    lockboxStatuses: lockboxStatusesFilter,
    sortBy: sort?.field ?? 'serialNumber',
    sortOrder: sort?.direction ?? 'asc',
    page,
    size: 20,
  };

  const setFiltersFromURLParams = (
    lockboxes: LockboxSearchForMLSStaffResponse['lockboxes'],
  ) => {
    const filterMap = {
      listingsFilter: {
        key: 'listing',
        setSelected: setSelectedListings,
        setSearched: setSearchedListings,
      },
      ownersFilter: {
        key: 'owner',
        setSelected: setSelectedOwners,
        setSearched: setSearchedOwners,
      },
      usersFilter: {
        key: 'assignee',
        setSelected: setSelectedUsers,
        setSearched: setSearchedOwners,
      },
      orgsFilter: {
        key: 'org',
        setSelected: setSelectedOrgs,
        setSearched: setSearchedOrgs,
      },
    } as const;

    Object.keys(filterMap).forEach((filter: keyof typeof filterMap) => {
      if (searchParam[filter]?.length) {
        const { key, setSelected, setSearched } = filterMap[filter];
        const map = lockboxes.reduce((acc: any, lockbox) => {
          if (lockbox[key]) {
            acc[lockbox[key]!.id] = lockbox[key];
          }
          return acc;
        }, {});
        // @ts-expect-error
        setSearched(Object.values(map));
        setSelected(map);
      }
    });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      const _csvLink = axios.getUri({
        url: '/lockbox_mls',
        method: 'get',
        params: { ...MLS_LOCKBOX_SEARCH_QUERY, exportCsv: 'true' },
      });
      setCsvLink(_csvLink);

      const response = await getMLSLockboxes<LockboxSearchForMLSStaffResponse>(
        MLS_LOCKBOX_SEARCH_QUERY,
      );

      if (isFirstRender) {
        setFiltersFromURLParams(response.lockboxes);
      }

      setSearchResult(response);
      setMlsLockboxes(response.lockboxes ?? []);
      setTotal(response.lockboxCount);
      setInitialTotal(response.totalLockboxCount);
      setLoading(false);
    })();

    setSearchParam({
      search: debouncedText || undefined,
      lockboxTypeFilter: arrayIfHasElements(lockboxTypeFilter),
      listingsFilter: arrayIfHasElements(listingsFilter),
      ownersFilter: arrayIfHasElements(ownersFilter),
      usersFilter: arrayIfHasElements(usersFilter),
      orgsFilter: arrayIfHasElements(orgsFilter),
      sort: sort?.direction && sort?.field ? sort : undefined,
    });
  }, [...searchDeps, page]);

  const debouncedListingSearch = React.useRef(
    debounce(async value => {
      if (!value) {
        setSearchedListings([]);
        return;
      }
      setFilterSearchLoading(true);
      const { data: listings } = await axios.get(
        `lockbox_mls/filters/listings/${encodeURIComponent(value)}`,
      );
      setSearchedListings(listings);
      setFilterSearchLoading(false);
    }, 500),
  ).current;
  React.useEffect(() => {
    debouncedListingSearch(listingInput);
  }, [debouncedListingSearch, listingInput]);

  const debouncedOwnerSearch = React.useRef(
    debounce(async value => {
      if (!value) {
        setSearchedOwners([]);
        return;
      }
      setFilterSearchLoading(true);
      const { data: owners } = await axios.get(
        `lockbox_mls/filters/owners/${encodeURIComponent(value)}`,
      );
      setSearchedOwners(owners);
      setFilterSearchLoading(false);
    }, 500),
  ).current;
  React.useEffect(() => {
    debouncedOwnerSearch(ownerInput);
  }, [debouncedOwnerSearch, ownerInput]);

  const debouncedUserSearch = React.useRef(
    debounce(async value => {
      if (!value) {
        setSearchedUsers([]);
        return;
      }
      setFilterSearchLoading(true);
      const { data: users } = await axios.get(
        `lockbox_mls/filters/users/${encodeURIComponent(value)}`,
      );
      setSearchedUsers(users);
      setFilterSearchLoading(false);
    }, 500),
  ).current;
  React.useEffect(() => {
    debouncedUserSearch(userInput);
  }, [debouncedUserSearch, userInput]);

  const debouncedOrgSearch = React.useRef(
    debounce(async value => {
      if (!value) {
        setSearchedOrgs([]);
        return;
      }
      setFilterSearchLoading(true);
      const { data: orgs } = await axios.get(
        `lockbox_mls/filters/brokerages/${encodeURIComponent(value)}`,
      );
      setSearchedOrgs(orgs);
      setFilterSearchLoading(false);
    }, 500),
  ).current;
  React.useEffect(() => {
    debouncedOrgSearch(orgInput);
  }, [debouncedOrgSearch, orgInput]);

  const StyledTextFilter = ({
    id,
    selectedKeys,
    setSelectedKeys,
    text = '(Blank)',
    allKeys,
  }: {
    id: Key;
    selectedKeys: Key[];
    setSelectedKeys: (value: Key[]) => void;
    text?: string;
    allKeys?: Key[];
  }) => (
    <StyledFilterSearchResult>
      <Checkbox
        checked={selectedKeys.includes(id)}
        value={id}
        onChange={() => {
          setSelectedKeys(
            selectedKeys.includes(id)
              ? selectedKeys.filter((key: string) => key !== id)
              : [...selectedKeys, id],
          );
        }}
      />
      <Text>{text}</Text>
    </StyledFilterSearchResult>
  );

  const filterConfirmButtonText = 'Apply';
  const filterResetButtonText = 'Reset';

  const handleReset = (setSelectedKeys: (keys: string[]) => void) => {
    setSelectedKeys([]);
  };

  const columns: TableProps['columns'] = ([
    // #region Box Type column
    {
      title: 'Box Type',
      dataIndex: 'lockboxType',
      key: 'lockboxType',
      width: 140,
      filteredValue: lockboxTypeFilter,
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [...LockboxTypes];
        return (
          <PaddedDropdownContainer>
            {[
              ...LockboxTypes.filter(
                lockboxType =>
                  lockboxTypeFilterOptions[
                    (['iBox', 'invoy'].includes(lockboxType)
                      ? 'supra'
                      : lockboxType) as keyof LockboxTypeFilterOptions
                  ],
              )
                .map(type => ({
                  text: lockboxTypeMap[type],
                  value: type,
                }))
                .sort((a, b) => a.text.localeCompare(b.text)),
            ].map(lockboxType => (
              <StyledTextFilter
                id={lockboxType.value}
                text={lockboxType.text}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                key={lockboxType.value}
                allKeys={allKeys}
              />
            ))}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: (lockboxType: LockboxType) => (
        <BrandContainer>
          <BrandLogo brand={lockboxType} />
          <EmphasisText>{lockboxTypeMap[lockboxType]}</EmphasisText>
        </BrandContainer>
      ),
    },
    // #endregion Box Type column
    // #region Serial column
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 140,
      sorter: false,
      sortOrder:
        sort?.field === 'serialNumber' ? `${sort?.direction}end` : null,
    },
    // #endregion Serial column
    // #region Listing column
    {
      title: 'Listing',
      dataIndex: 'listing',
      key: 'listing',
      width: 250,
      filteredValue: listingsFilter,
      onFilterDropdownOpenChange: () => setListingInput(''),
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [
          ...listingsFilter,
          ...searchedListings.map(searched => searched.id),
          '(Blank)',
        ];
        return (
          <PaddedDropdownContainer>
            <Input
              placeholder={`Search Listings`}
              onChange={element => setListingInput(element.target.value)}
              value={listingInput}
              allowClear
            />
            <StyledTextFilter
              id="(Blank)"
              text={'Unassigned'}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              allKeys={allKeys}
            />
            {[
              ...Object.values(selectedListings)
                .filter(l => l.id !== '(Blank)')
                .map(selectedListing => (
                  <StyledFilterListing
                    key={selectedListing.id}
                    listing={selectedListing}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                  />
                )),
              ...searchedListings
                .filter(sl => !selectedListings[sl.id])
                .map(searchedListing => (
                  <StyledFilterListing
                    key={searchedListing.id}
                    listing={searchedListing}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                  />
                )),
            ]}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                  setListingInput('');
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: listing => {
        if (!listing) return <Unassigned />;
        const { thumbnail, address } = listing;
        return (
          <StyledContainer>
            <ListingImage imageSrc={thumbnail} size={24} fixProportion />
            {address}
          </StyledContainer>
        );
      },
    },
    // #endregion Listing column
    // #region Owner column
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 220,
      filteredValue: ownersFilter,
      onFilterDropdownOpenChange: () => setOwnerInput(''),
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [
          ...Object.values(selectedOwners).map(owner => owner.id),
          ...searchedOwners.map(owner => owner.id),
          '(Blank)',
        ];
        return (
          <PaddedDropdownContainer>
            <Input
              placeholder={`Search Owners`}
              onChange={element => setOwnerInput(element.target.value)}
              value={ownerInput}
              allowClear
            />
            <StyledTextFilter
              id="(Blank)"
              text={'Unassigned'}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              allKeys={allKeys}
            />
            {[
              ...Object.values(selectedOwners)
                .filter(o => o.id !== '(Blank)')
                .map(selectedOwner => (
                  <StyledFilterUser
                    key={selectedOwner.id}
                    user={selectedOwner}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
              ...searchedOwners
                .filter(so => !selectedOwners[so.id])
                .map(searchedOwner => (
                  <StyledFilterUser
                    key={searchedOwner.id}
                    user={searchedOwner}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
            ]}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                  setOwnerInput('');
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: (owner, record) => {
        if (!owner) return <Unassigned />;
        const { assignee, ownerType, assigneeType } = record ?? {};

        // Derive officeName only when officeName exists in the ES response
        let officeName = '';
        const ownerSource =
          ownerType === 'office'
            ? 'owner'
            : assigneeType === 'office'
              ? 'assignee'
              : null;
        if (assigneeType === 'office') {
          officeName = (assignee as any)?.officeName || '';
        }
        if (ownerType === 'office') {
          officeName = (owner as any)?.officeName || '';
        }

        // Office owner: show officeName (if present) above OID
        if (officeName && ownerSource) {
          const oid = record?.[ownerSource]?.oid?.[0];
          return (
            <StyledContainer>
              <AvatarContainer>
                <Avatar
                  user={{ ...owner, firstName: officeName, lastName: '' }}
                  width={32}
                />
              </AvatarContainer>
              <div>
                <div>{officeName}</div>
                {oid && <OwnerSubText>OID: {oid}</OwnerSubText>}
              </div>
            </StyledContainer>
          );
        }

        // Default: show owner name / OID with avatar initial
        const displayName =
          owner.name ||
          [owner?.firstName, owner?.lastName].filter(Boolean).join(' ') ||
          '-';
        return (
          <StyledContainer>
            <AvatarContainer>
              <Avatar
                user={
                  owner.firstName
                    ? owner
                    : { ...owner, firstName: owner.name || '' }
                }
                width={32}
              />
            </AvatarContainer>
            {displayName}
          </StyledContainer>
        );
      },
    },
    // #endregion Owner column
    // #region Assignee column
    {
      title: 'Assignee',
      dataIndex: 'assignee',
      key: 'assignee',
      width: 220,
      filteredValue: usersFilter,
      onFilterDropdownOpenChange: () => setUserInput(''),
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [
          ...Object.values(selectedUsers).map(selected => selected.id),
          ...searchedUsers.map(searched => searched.id),
          '(Blank)',
        ];
        return (
          <PaddedDropdownContainer>
            <Input
              placeholder={`Search Users`}
              onChange={element => setUserInput(element.target.value)}
              value={userInput}
              allowClear
            />
            <StyledTextFilter
              id="(Blank)"
              text={'Unassigned'}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              allKeys={allKeys}
            />
            {[
              ...Object.values(selectedUsers)
                .filter(u => u.id !== '(Blank)')
                .map(selectedUser => (
                  <StyledFilterUser
                    key={selectedUser.id}
                    user={selectedUser}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    allKeys={allKeys}
                  />
                )),
              ...searchedUsers
                .filter(su => !selectedUsers[su.id])
                .map(searchedUser => (
                  <StyledFilterUser
                    key={searchedUser.id}
                    user={searchedUser}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    allKeys={allKeys}
                  />
                )),
            ]}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                  setUserInput('');
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: assignee => {
        if (!assignee) return <Unassigned />;
        return (
          <StyledContainer>
            <AvatarContainer>
              <Avatar
                user={
                  assignee.firstName
                    ? assignee
                    : { ...assignee, firstName: assignee.name }
                }
                width={32}
              />
            </AvatarContainer>
            {assignee.name || `${assignee?.firstName} ${assignee?.lastName}`}
          </StyledContainer>
        );
      },
    },
    // #endregion Assignee column
    // #region Brokerage column
    {
      title: 'Brokerage',
      dataIndex: 'org',
      key: 'org',
      width: 220,
      filteredValue: orgsFilter,
      onFilterDropdownOpenChange: () => setOrgInput(''),
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [
          ...Object.values(selectedOrgs).map(selected => selected.id),
          ...searchedOrgs.map(searched => searched.id),
          '(Blank)',
        ];
        return (
          <PaddedDropdownContainer>
            <Input
              placeholder={`Search Brokerages`}
              onChange={element => setOrgInput(element.target.value)}
              value={orgInput}
              allowClear
            />
            <StyledTextFilter
              id="(Blank)"
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              allKeys={allKeys}
            />
            {[
              ...Object.values(selectedOrgs)
                .filter(o => o.id !== '(Blank)')
                .map(selectedOrg => (
                  <StyledFilterUser
                    key={selectedOrg.id}
                    user={selectedOrg}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
              ...searchedOrgs
                .filter(so => !selectedOrgs[so.id])
                .map(searchedOrg => (
                  <StyledFilterUser
                    key={searchedOrg.id}
                    user={searchedOrg}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
            ]}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                  setOrgInput('');
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: org => org?.name,
    },
    // #endregion Brokerage column
    // #region Supra column
    {
      title: 'Supra',
      dataIndex: 'ownershipStatus',
      key: 'ownershipStatus',
      width: 150,
      filteredValue: ownershipStatusesFilter,
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = ['(Blank)', ...OwnershipStatuses];
        return (
          <PaddedDropdownContainer>
            {[
              ...OwnershipStatuses.map(status => ({
                text: status,
                value: status,
              })).sort((a, b) => a.text.localeCompare(b.text)),
              { text: '(Blank)', value: '(Blank)' },
            ].map(license => (
              <StyledTextFilter
                id={license.value}
                text={license.text}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                allKeys={allKeys}
                key={license.value}
              />
            ))}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: (ownershipStatus, { isLost, isDefective }) => (
        <LockboxLicenseTag
          ownershipStatus={ownershipStatus}
          isLost={isLost}
          isDefective={isDefective}
        />
      ),
    },
    // #endregion Supra column
    // #region Resale column
    {
      title: 'Resale',
      dataIndex: 'resaleStatus',
      key: 'resaleStatus',
      width: 150,
      filteredValue: resaleStatusesFilter,
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = ['(Blank)', ...ResaleStatuses];
        return (
          <PaddedDropdownContainer>
            {[
              ...ResaleStatuses.map(status => ({
                text: status,
                value: status,
              })).sort((a, b) => a.text.localeCompare(b.text)),
              { text: '(Blank)', value: '(Blank)' },
            ].map(resale => (
              <StyledTextFilter
                id={resale.value}
                text={resale.text}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                allKeys={allKeys}
                key={resale.value}
              />
            ))}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: resaleStatus => <ResaleStatusTag resaleStatus={resaleStatus} />,
    },
    // #endregion Resale column
    // #region (lockbox) Status column
    {
      title: 'Status',
      dataIndex: 'lockboxStatus',
      key: 'lockboxStatus',
      width: 150,
      filteredValue: lockboxStatusesFilter,
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = ['(Blank)', ...LockboxStatuses];
        return (
          <PaddedDropdownContainer>
            {[
              ...LockboxStatuses.map(status => ({
                text: status,
                value: status,
              })).sort((a, b) => a.text.localeCompare(b.text)),
              { text: '(Blank)', value: '(Blank)' },
            ].map(status => (
              <StyledTextFilter
                id={status.value}
                text={status.text}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                allKeys={allKeys}
                key={status.value}
              />
            ))}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: lockboxStatus => (
        <LockboxStatusTag lockboxStatus={lockboxStatus} />
      ),
    },
    // #endregion Status
    // #region Battery% column
    {
      title: 'Battery %',
      dataIndex: 'deviceBatteryLevel',
      key: 'deviceBatteryLevel',
      width: 140,
      sorter: true,
      sortOrder:
        sort?.field === 'deviceBatteryLevel' ? `${sort?.direction}end` : null,
      render: deviceBatteryLevel =>
        deviceBatteryLevel ? `${deviceBatteryLevel}%` : '',
    },
    // #endregion Battery% column
    // #region Actions column
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, lockbox) => (
        <ButtonContainer>
          {lockbox.brand === 'supraLockbox' ? (
            <ActionText>Activity</ActionText>
          ) : (
            <>
              <ActionText
                disabled={lockbox.brand === 'supraLockbox'}
                onClick={() => {
                  if (lockbox.brand === 'supraLockbox') return;
                  setLockboxToDelete(lockbox);
                  setDeleteModalOpen(true);
                }}
              >
                Delete
              </ActionText>
            </>
          )}
        </ButtonContainer>
      ),
    },
    // #endregion Actions column
  ] as TableProps['columns'])!.filter(Boolean);

  return (
    <>
      <SearchTable
        rowKey={record => record._id}
        columns={columns}
        loading={loading}
        dataSource={searchResult?.lockboxes ?? []}
        totalItems={searchResult?.lockboxCount || 0}
        onRow={lockbox => ({
          ...(lockbox.brand === 'supraLockbox'
            ? {
                onClick: () => {
                  history.push(`/mls/lockbox/${lockbox._id}`);
                },
              }
            : {}),
          'data-cy': `lockbox-row-${lockbox._id}`,
        })}
        onChange={(_, filters, sorter) => {
          const toBeSelectedListings: Record<
            string,
            NonNullable<ESLockbox['listing']>
          > = listingsFilter.reduce((obj, oid) => {
            if (filters.listing?.includes(oid)) {
              obj[oid] = selectedListings[oid]; // eslint-disable-line no-param-reassign
            }
            return obj;
          }, {} as any);

          const toBeSelectedOwners: Record<
            string,
            NonNullable<ESLockbox['owner']>
          > = ownersFilter.reduce((obj, oid) => {
            if (filters.owner?.includes(oid)) {
              obj[oid] = selectedOwners[oid]; // eslint-disable-line no-param-reassign
            }
            return obj;
          }, {} as any);

          const toBeSelectedUsers: Record<
            string,
            NonNullable<ESLockbox['assignee']>
          > = usersFilter.reduce((obj, oid) => {
            if (filters.assignee?.includes(oid)) {
              obj[oid] = selectedUsers[oid]; // eslint-disable-line no-param-reassign
            }
            return obj;
          }, {} as any);

          const toBeSelectedOrgs: Record<
            string,
            NonNullable<ESLockbox['org']>
          > = orgsFilter.reduce((obj, oid) => {
            if (filters.org?.includes(oid)) {
              obj[oid] = selectedOrgs[oid]; // eslint-disable-line no-param-reassign
            }
            return obj;
          }, {} as any);

          searchedListings.forEach(searchedListing => {
            if (
              filters.listing?.find(
                filteredListing => filteredListing === searchedListing.id,
              )
            ) {
              toBeSelectedListings[searchedListing.id] = searchedListing;
            }
          });
          if (filters.listing?.includes('(Blank)')) {
            toBeSelectedListings['(Blank)'] = {
              id: '(Blank)',
              line1: '',
              line2: '',
              city: '',
              province: '',
            };
          }

          searchedOwners.forEach(searchedOwner => {
            if (
              filters.owner?.find(
                filteredOwner => filteredOwner === searchedOwner.id,
              )
            ) {
              toBeSelectedOwners[searchedOwner.id] = searchedOwner;
            }
          });
          if (filters.owner?.includes('(Blank)')) {
            toBeSelectedOwners['(Blank)'] = { id: '(Blank)' };
          }

          searchedUsers.forEach(searchedUser => {
            if (
              filters.assignee?.find(
                filteredUser => filteredUser === searchedUser.id,
              )
            ) {
              toBeSelectedUsers[searchedUser.id] = searchedUser;
            }
          });
          if (filters.assignee?.includes('(Blank)')) {
            toBeSelectedUsers['(Blank)'] = { id: '(Blank)', name: '(Blank)' };
          }

          searchedOrgs.forEach(searchedOrg => {
            if (
              filters.org?.find(filteredOrg => filteredOrg === searchedOrg.id)
            ) {
              toBeSelectedOrgs[searchedOrg.id] = searchedOrg;
            }
          });
          if (filters.org?.includes('(Blank)')) {
            toBeSelectedOrgs['(Blank)'] = { id: '(Blank)', name: '(Blank)' };
          }

          // Set filter values
          setSelectedListings(toBeSelectedListings);
          setSelectedOwners(toBeSelectedOwners);
          setSelectedUsers(toBeSelectedUsers);
          setSelectedOrgs(toBeSelectedOrgs);
          setLockboxTypeFilter(filters.lockboxType as LockboxType[]);
          setOwnershipStatusesFilter(
            filters.ownershipStatus as OwnershipStatusType[],
          );
          setResaleStatusesFilter(filters.resaleStatus as ResaleStatusType[]);
          setLockboxStatusesFilter(
            filters.lockboxStatus as LockboxStatusType[],
          );

          // Set sorter
          if ((sorter as any).field && (sorter as any).order) {
            setSort({
              field: (sorter as any).field,
              direction: (sorter as any).order === 'descend' ? 'desc' : 'asc',
            });
          } else {
            setSort(null);
          }

          setPage(0);
        }}
        rowSelection={{
          type: 'checkbox',
          onChange: (_, selectedRows) => {
            if (!selectedRows.length) {
              setSelectedLockboxes([]);
            } else {
              setSelectedLockboxes(
                selectedRows
                  .filter(lockbox => lockbox.brand === 'supraLockbox')
                  .map(({ _id }) => _id),
              );
            }
          },
          getCheckboxProps: record => ({
            id: record.id,
          }),
        }}
      />

      <Modal
        title="Are you sure?"
        open={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
        }}
        footer={
          <div>
            <Button
              disabled={deleteLoading}
              onClick={() => {
                setDeleteModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              loading={deleteLoading}
              type="primary"
              onClick={async () => {
                setDeleteLoading(true);
                if (lockboxToDelete) {
                  try {
                    await deleteLockbox(lockboxToDelete._id);
                  } catch (err) {
                    message.error('Error deleting lockbox');
                    errorReporter.error(
                      `Error deleting lockbox: ${lockboxToDelete._id}: ${err}`,
                    );
                  } finally {
                    setDeleteLoading(false);
                  }
                }
                setDeleteModalOpen(false);
              }}
            >
              Delete
            </Button>
          </div>
        }
      >
        Are you sure you wish to delete this lockbox?
        <div>
          {lockboxToDelete &&
            `${brandMap[lockboxToDelete.brand]}: ${
              lockboxToDelete.serialNumber
            }`}
        </div>
      </Modal>
    </>
  );
};

// #region Styled components
const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarContainer = styled.div`
  min-width: 32px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const BrandContainer = styled(StyledContainer)`
  gap: 13px;
`;

const EmphasisText = styled.div`
  font-weight: 500;
`;

const OwnerSubText = styled.div`
  font-size: 14px;
`;

const PaddedDropdownContainer = styled.div`
  padding: 8px;
`;

// #endregion

export default MLSStaffLockboxesTable;
